import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import noop from 'lodash/noop';
import { themeGet, borders, borderRadius, color } from 'styled-system';
import { Box, Icon, Input as BaseInput } from 'roo-ui/components';
import { mediaQuery, cursor } from 'lib/styledSystem';

const getAlto = themeGet('colors.greys.alto');
const getBrandSecondary = themeGet('colors.brand.secondary');

const InputIcon = styled(Icon)`
  position: absolute;
  left: ${(p) => p.theme.space[3]};
  top: 50%;
  transform: translateY(-50%);
  ${cursor};
`;

// We need to override the readonly styles here, until roo-ui relaxes the styles they apply
const Input = styled(BaseInput)`
  ${borders};
  ${borderRadius};
  ${color};
  flex: 1;
  font-size: ${themeGet('fontSizes.base')};
  margin: 0;
  padding: ${themeGet('space.3')} ${themeGet('space.4')};
  border-color: ${(props) => (props.isHighlighted ? getBrandSecondary(props) : getAlto(props))};

  ${(props) =>
    props.hasIcon &&
    css`
      padding-left: ${themeGet('space.10')(props)};
    `};

  ${(props) =>
    props.noBorder === 'right' &&
    css`
      border-right: none;
    `};

  ${(props) =>
    props.noBorder === 'left' &&
    css`
      border-left: none;
    `};

  ${cursor};

  ${mediaQuery.minWidth.sm} {
    border-color: ${(props) => (props.isHighlighted ? getBrandSecondary(props) : getAlto(props))};
  }

  &[readonly] {
    ${borders};
    ${borderRadius};
    ${color};
    padding: ${themeGet('space.3')} ${themeGet('space.4')};
    border-color: ${(props) => (props.isHighlighted ? getBrandSecondary(props) : getAlto(props))};

    ${(props) =>
      props.hasIcon &&
      css`
        padding-left: ${themeGet('space.10')(props)};
      `};

    ${(props) =>
      props.noBorder === 'right' &&
      css`
        border-right: none;
      `};

    ${(props) =>
      props.noBorder === 'left' &&
      css`
        border-left: none;
      `};

    ${mediaQuery.minWidth.sm} {
      border-color: ${(props) => (props.isHighlighted ? getBrandSecondary(props) : getAlto(props))};
    }
  }
`;

Input.defaultProps = {
  ...BaseInput.defaultProps,
  ...borders.defaultProps,
  ...borderRadius.defaultProps,
  ...color.defaultProps,
};

const InputWithIcon = React.forwardRef(({ icon, cursor, ...rest }, ref) => {
  return (
    <Box position="relative">
      {icon && <InputIcon name={icon} {...cursor} />}
      <Input ref={ref} hasIcon={!!icon} {...rest} />
    </Box>
  );
});

InputWithIcon.propTypes = {
  icon: PropTypes.string,
  cursor: PropTypes.string,
  isHighlighted: PropTypes.bool,
  readonly: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
};

InputWithIcon.defaultProps = {
  icon: undefined,
  cursor: undefined,
  isHighlighted: false,
  readonly: false,
  value: '',
  onFocus: noop,
  onBlur: noop,
  onChange: noop,
};

InputWithIcon.displayName = 'InputWithIcon';

export default InputWithIcon;
